import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Warning, CheckCircle, X, Plus } from "@phosphor-icons/react";

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromAddress, setFromAddress] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [sendWeekday, setSendWeekday] = useState(0);
  const [sendHour, setSendHour] = useState(8);
  const [unreadEnabled, setUnreadEnabled] = useState(false);
  const [unreadHours, setUnreadHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingUnread, setSendingUnread] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await API.get("/email-settings");
      setSettings(data);
      setFromAddress(data.from_address || "");
      setRecipients(data.recipients || []);
      setEnabled(!!(data.weekly_enabled ?? data.monthly_enabled));
      setSendWeekday(data.weekly_send_weekday ?? 0);
      setSendHour(data.weekly_send_hour ?? 8);
      setUnreadEnabled(!!data.unread_reminders_enabled);
      setUnreadHours(data.unread_threshold_hours || 24);
    } catch {
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const addRecipient = () => {
    const v = newRecipient.trim();
    if (!v) return;
    if (recipients.includes(v)) { toast.warning("Already added"); return; }
    setRecipients([...recipients, v]);
    setNewRecipient("");
  };

  const removeRecipient = (r) => setRecipients(recipients.filter((x) => x !== r));

  const persistSettings = async () => {
    const { data } = await API.put("/email-settings", {
      from_address: fromAddress.trim(),
      recipients,
      weekly_enabled: enabled,
      weekly_send_weekday: Number(sendWeekday),
      weekly_send_hour: Number(sendHour),
      unread_reminders_enabled: unreadEnabled,
      unread_threshold_hours: Number(unreadHours) || 24,
    });
    setSettings(data);
    return data;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistSettings();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      // Persist current recipients/schedule first — the server sends to the
      // SAVED recipients, so an unsaved email would otherwise be ignored.
      await persistSettings();
      const { data } = await API.post("/email-settings/send-weekly-now");
      if (data.skipped) {
        toast.warning(data.reason || "Skipped");
      } else if (data.ok) {
        toast.success(`Sent ${data.invoice_count} invoice${data.invoice_count === 1 ? "" : "s"} for ${data.period} (${data.mode})`);
        fetchSettings();
      } else {
        toast.error(data.error || "Failed to send");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await API.post("/email-settings/send-test");
      if (data.delivered) {
        toast.success(data.message);
      } else {
        toast.warning(data.message || "Not delivered");
      }
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test");
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendUnreadNow = async () => {
    setSendingUnread(true);
    try {
      const { data } = await API.post("/email-settings/send-unread-reminders-now");
      if (data.skipped) {
        toast.info(data.reason || "Nothing to send");
      } else if (data.ok) {
        toast.success(`Reminded about ${data.count} message${data.count === 1 ? "" : "s"} across ${data.solicitors} solicitor${data.solicitors === 1 ? "" : "s"}`);
        fetchSettings();
      } else {
        toast.error(data.error || "Failed to send reminders");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSendingUnread(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="email-settings-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Email & Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-send weekly referral statements to your accountant or admin team, every Monday morning.</p>
        </div>

        {/* Delivery status banner — we send via Outlook (Graph). */}
        {(() => {
          const users = settings?.connected_outlook_users || [];
          const outlookReady = settings?.outlook_configured && settings?.system_sender_healthy;
          const staleConn = settings?.outlook_configured && users.length > 0 && !settings?.system_sender_healthy;
          const canSend = settings?.provider_configured || outlookReady;
          return (
        <div className={`rounded-lg p-4 border flex items-start gap-3 ${
          canSend
            ? "bg-emerald-50 border-emerald-200"
            : "bg-amber-50 border-amber-200"
        }`} data-testid="provider-status">
          {canSend ? (
            <CheckCircle weight="fill" className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          ) : (
            <Warning weight="fill" className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            <p className={`font-semibold ${canSend ? "text-emerald-800" : "text-amber-800"}`}>
              {canSend
                ? (outlookReady ? "Outlook connected — statements send for real" : "Real email provider configured")
                : (staleConn ? "Outlook connection expired — reconnect to send" : "Not connected — emails won't actually send")}
            </p>
            <p className={`mt-0.5 ${canSend ? "text-emerald-700" : "text-amber-700"}`}>
              {canSend
                ? "Statements are delivered from your firm's Outlook mailbox (Microsoft Graph)."
                : staleConn
                  ? "Your Outlook token has expired or been revoked, so emails silently fall back to the mock outbox. Reconnect Outlook from your profile to restore live delivery."
                  : "Connect Outlook and pick a system sender below (or add a Resend/SendGrid key) — until then emails are only saved to /tmp/email_outbox/ for inspection."}
            </p>
          </div>
        </div>
          );
        })()}

        {/* Settings form */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5">
          <div>
            <Label htmlFor="from-address" className="text-xs font-medium text-slate-700">From address</Label>
            <Input id="from-address" data-testid="from-address-input" value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)} placeholder="noreply@yourdomain.com"
              className="mt-1.5" />
            <p className="text-[11px] text-slate-400 mt-1">Must be a verified sender at your provider once enabled.</p>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-700">Recipients</Label>
            <div className="flex flex-wrap gap-2 mt-1.5 mb-2" data-testid="recipients-list">
              {recipients.length === 0 && <p className="text-[11px] text-slate-400">No recipients yet. Add at least one to enable weekly sends.</p>}
              {recipients.map((r) => (
                <span key={r} data-testid={`recipient-${r}`} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 bg-slate-100 rounded-full text-xs text-slate-700">
                  <EnvelopeSimple weight="bold" className="w-3 h-3 text-slate-500" />
                  {r}
                  <button onClick={() => removeRecipient(r)} className="ml-0.5 p-0.5 rounded-full hover:bg-slate-200" data-testid={`remove-${r}`}>
                    <X weight="bold" className="w-3 h-3 text-slate-500" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="new-recipient-input" value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
                placeholder="accountant@example.com" type="email" />
              <Button onClick={addRecipient} variant="outline" size="default" data-testid="add-recipient-btn" className="shrink-0">
                <Plus weight="bold" className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200">
            <div>
              <p className="text-sm font-medium text-slate-900">Weekly statement</p>
              <p className="text-[11px] text-slate-500">Auto-emails the prior week's unpaid invoices as PDF + CSV via Outlook.</p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="weekly-enabled-switch" />
          </div>

          {enabled && (
            <div className="flex flex-wrap items-end gap-4 -mt-1 pl-1" data-testid="weekly-schedule-controls">
              <div>
                <Label className="text-[11px] font-medium text-slate-600">Send day</Label>
                <select
                  value={sendWeekday}
                  onChange={(e) => setSendWeekday(Number(e.target.value))}
                  data-testid="weekly-send-weekday"
                  className="mt-1 block h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                    <option key={d} value={i}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-[11px] font-medium text-slate-600">Send time (UTC)</Label>
                <select
                  value={sendHour}
                  onChange={(e) => setSendHour(Number(e.target.value))}
                  data-testid="weekly-send-hour"
                  className="mt-1 block h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-slate-400 pb-2">Runs every {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"][sendWeekday]} at {String(sendHour).padStart(2,"0")}:00 UTC. Save to apply.</p>
            </div>
          )}

          {settings?.last_sent_at && (
            <div className="text-[11px] text-slate-500 border-l-2 border-slate-200 pl-3" data-testid="last-sent-info">
              Last sent: {new Date(settings.last_sent_at).toLocaleString("en-GB")} ·
              {" "}{settings.last_sent_count} invoice{settings.last_sent_count === 1 ? "" : "s"} ·
              {" "}{(settings.last_sent_to || []).join(", ")}
            </div>
          )}
          {settings?.last_error && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2" data-testid="last-error">
              Last error: {settings.last_error}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <Button onClick={handleSendNow} disabled={sending || recipients.length === 0} variant="outline" data-testid="send-now-btn">
                <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                {sending ? "Sending..." : "Send Now (last 7 days)"}
              </Button>
              <Button onClick={handleSendTest} disabled={sendingTest} variant="ghost" className="text-slate-600" data-testid="send-test-btn">
                {sendingTest ? "Sending..." : "Email a sample to me"}
              </Button>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-settings-btn">
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>

        {/* Unread Message Reminders */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5" data-testid="unread-reminders-card">
          <div>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Unread message reminders</h2>
            <p className="text-xs text-slate-500 mt-1">Email each solicitor a digest of client and referrer messages they haven't read in <strong>{unreadHours}</strong> hour{Number(unreadHours) === 1 ? "" : "s"}. Each message is reminded only once.</p>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md border border-slate-200">
            <div>
              <p className="text-sm font-medium text-slate-900">Auto-remind solicitors hourly</p>
              <p className="text-[11px] text-slate-500">Background sweep runs every hour while enabled.</p>
            </div>
            <Switch checked={unreadEnabled} onCheckedChange={setUnreadEnabled} data-testid="unread-enabled-switch" />
          </div>

          <div>
            <Label htmlFor="unread-hours" className="text-xs font-medium text-slate-700">Threshold (hours)</Label>
            <Input id="unread-hours" data-testid="unread-hours-input" type="number" min="1" max="168"
              value={unreadHours}
              onChange={(e) => setUnreadHours(e.target.value)}
              className="mt-1.5 max-w-[180px]" />
            <p className="text-[11px] text-slate-400 mt-1">Send a reminder once a message is unread for this long. 1–168 hours.</p>
          </div>

          {settings?.unread_last_run_at && (
            <div className="text-[11px] text-slate-500 border-l-2 border-slate-200 pl-3" data-testid="unread-last-run">
              Last sweep: {new Date(settings.unread_last_run_at).toLocaleString("en-GB")} ·
              {" "}{settings.unread_last_sent_count} reminder{settings.unread_last_sent_count === 1 ? "" : "s"} sent
            </div>
          )}
          {settings?.unread_last_error && (
            <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded p-2" data-testid="unread-last-error">
              Last error: {settings.unread_last_error}
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-slate-100">
            <Button onClick={handleSendUnreadNow} disabled={sendingUnread} variant="outline" data-testid="send-unread-now-btn">
              <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5 mr-1.5" />
              {sendingUnread ? "Sending..." : "Run sweep now"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
